import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Skeleton } from '@/components/ui/skeleton';
import { usePlanVsActual } from '@/hooks/use-projects';
import type { PlanVsActualItem } from '@hygge/shared';
import { BarChart3 } from 'lucide-react';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function varianceColor(percent: number): string {
  const abs = Math.abs(percent);
  if (abs <= 0) return 'text-emerald-600 dark:text-emerald-400';
  if (abs <= 20) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}


interface PlanVsActualCardProps {
  projectId: string;
  month: string;
}

export function PlanVsActualCard({ projectId, month }: PlanVsActualCardProps) {
  const { data, isLoading } = usePlanVsActual(projectId, month);

  if (isLoading) {
    return <Skeleton className="h-64 rounded-lg" />;
  }

  if (!data || data.items.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="mb-4 flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">Plan vs Actual</h4>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-2.5 text-left font-medium" rowSpan={2}>Name</th>
              <th className="px-3 py-1.5 text-center font-medium" colSpan={3}>Hours</th>
              <th className="px-3 py-1.5 text-center font-medium" colSpan={3}>Cost</th>
              <th className="px-3 py-1.5 text-center font-medium" colSpan={3}>Revenue</th>
            </tr>
            <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
              <th className="px-3 py-1.5 text-right">Plan</th>
              <th className="px-3 py-1.5 text-right">Actual</th>
              <th className="px-3 py-1.5 text-right">Var %</th>
              <th className="px-3 py-1.5 text-right">Plan</th>
              <th className="px-3 py-1.5 text-right">Actual</th>
              <th className="px-3 py-1.5 text-right">Var %</th>
              <th className="px-3 py-1.5 text-right">Plan</th>
              <th className="px-3 py-1.5 text-right">Actual</th>
              <th className="px-3 py-1.5 text-right">Var %</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <PlanVsActualRow key={item.employeeId} item={item} />
            ))}

            {/* Totals row */}
            <tr className="border-t-2 bg-primary/5 font-bold">
              <td className="px-4 py-3">Total</td>
              <td className="px-3 py-3 text-right font-mono">{data.totals.planned.hours.toFixed(1)}</td>
              <td className="px-3 py-3 text-right font-mono">{data.totals.actual.hours.toFixed(1)}</td>
              <td className={`px-3 py-3 text-right font-mono ${varianceColor(data.totals.variance.hoursPercent)}`}>
                {data.totals.variance.hoursPercent.toFixed(1)}%
              </td>
              <td className="px-3 py-3 text-right font-mono">{formatCurrency(data.totals.planned.cost)}</td>
              <td className="px-3 py-3 text-right font-mono">{formatCurrency(data.totals.actual.cost)}</td>
              <td className={`px-3 py-3 text-right font-mono ${varianceColor(data.totals.variance.costPercent)}`}>
                {data.totals.variance.costPercent.toFixed(1)}%
              </td>
              <td className="px-3 py-3 text-right font-mono">{formatCurrency(data.totals.planned.revenue)}</td>
              <td className="px-3 py-3 text-right font-mono">{formatCurrency(data.totals.actual.revenue)}</td>
              <td className={`px-3 py-3 text-right font-mono ${varianceColor(data.totals.variance.revenuePercent)}`}>
                {data.totals.variance.revenuePercent.toFixed(1)}%
              </td>
            </tr>

            {/* Margin comparison */}
            <tr className="bg-muted/30 text-xs">
              <td className="px-4 py-2 font-medium text-muted-foreground" colSpan={4}>Margin Coefficient</td>
              <td className="px-3 py-2 text-right font-mono" colSpan={3}>
                Planned: {data.totals.plannedMarginCoefficient.toFixed(2)}x
              </td>
              <td className="px-3 py-2 text-right font-mono" colSpan={3}>
                Actual: {data.totals.actualMarginCoefficient.toFixed(2)}x
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlanVsActualRow({ item }: { item: PlanVsActualItem }) {
  const formatName = useEmployeeNameFormatter();
  return (
    <tr className="border-b last:border-b-0 hover:bg-muted/30">
      <td className="px-4 py-1.5">
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6">
            <AvatarImage src={item.avatarUrl ?? undefined} />
            <AvatarFallback className="text-[10px]">
              {item.firstName?.[0]}{item.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium">
            {formatName({ firstName: item.firstName, lastName: item.lastName })}
          </span>
        </div>
      </td>
      {/* Hours */}
      <td className="px-3 py-1.5 text-right font-mono">{item.planned.hours.toFixed(1)}</td>
      <td className="px-3 py-1.5 text-right font-mono">{item.actual.hours.toFixed(1)}</td>
      <td className={`px-3 py-1.5 text-right font-mono text-xs ${varianceColor(item.variance.hoursPercent)}`}>
        {item.variance.hoursPercent.toFixed(1)}%
      </td>
      {/* Cost */}
      <td className="px-3 py-1.5 text-right font-mono">{formatCurrency(item.planned.cost)}</td>
      <td className="px-3 py-1.5 text-right font-mono">{formatCurrency(item.actual.cost)}</td>
      <td className={`px-3 py-1.5 text-right font-mono text-xs ${varianceColor(item.variance.costPercent)}`}>
        {item.variance.costPercent.toFixed(1)}%
      </td>
      {/* Revenue */}
      <td className="px-3 py-1.5 text-right font-mono">{formatCurrency(item.planned.revenue)}</td>
      <td className="px-3 py-1.5 text-right font-mono">{formatCurrency(item.actual.revenue)}</td>
      <td className={`px-3 py-1.5 text-right font-mono text-xs ${varianceColor(item.variance.revenuePercent)}`}>
        {item.variance.revenuePercent.toFixed(1)}%
      </td>
    </tr>
  );
}
